export const FIRE_SIGNALS_ROOT = '/normal-operations/risk-warning/fire-signals';
export const FIRE_SIGNALS_WARNING =
  'HISTORICAL SATELLITE OBSERVATION / RESEARCH CONTEXT / NOT A CONFIRMED FIRE / NOT FOR RESCUE USE';
export type Bbox = [number, number, number, number];
export interface Station {
  stationId: string;
  name: string;
  latitude: number;
  longitude: number;
  sourceRevision: string;
  originalUri: string;
}
export interface Satellite {
  detectionId: string;
  provider: string;
  satellite: string;
  instrument: string;
  productVersion: string;
  sourceRevision: string;
  acquiredAtUTC: string;
  rawRowNumber: number;
  rawRowHash: string;
  originalHash: string;
  originalUri: string;
  latitude: number;
  longitude: number;
  crsEPSG: number;
  pixelScanKm: number | null;
  pixelTrackKm: number | null;
  nativeConfidence: string;
  frpMW: number | null;
  brightnessTi4Kelvin: number | null;
  brightnessTi5Kelvin: number | null;
  dayNight: string;
}
export interface WeatherLink {
  observationId: string;
  stationId: string;
  latitude: number;
  longitude: number;
  metric: string;
  unit: string;
  value: number;
  observedAtUTC: string;
  rawObservedAtUTC: string;
  quality: string;
  sourceRevision: string;
  originalHash: string;
  originalUri: string;
  rawRowNumber: number;
  rawRowHash: string;
  rawQualityCode: string;
  sourceCode: string;
  archiveReceivedAt: string;
  distanceKm: number;
  ageMinutes: number;
}
export type AssociationStatus =
  'WEATHER_CONTEXT_LINKED' | 'NO_QUALIFIED_WEATHER' | 'SATELLITE_ONLY';
export interface Candidate {
  runId: string;
  candidateId: string;
  areaRef: string;
  sourceRevision: string;
  ruleVersion: string;
  ruleHash: string;
  mode: string;
  satellite: Satellite;
  weather: {
    stationId: string | null;
    station: Station | null;
    metrics: { TEMPERATURE: WeatherLink | null; WIND_SPEED: WeatherLink | null };
    archiveReceivedAt: string;
  };
  association: {
    status: AssociationStatus;
    eligibilityReason: string;
    context: string;
    distanceLimitKm: number;
    timeLimitHours: number;
    qualifyingMetricSet: string[];
    stationDecisions: unknown[];
  };
  eventReference: { status: string; reason: string; retrospectiveOnly: boolean };
  lifecycle: { evaluationKind: string; configSnapshotHash: string };
}
export interface Source {
  sourceId: string;
  provider: string;
  sourceRevision: string;
  originalHash: string;
  originalUri: string;
  retrievedAt: string;
  receivedAt: string;
  sourceArchivePublishedAt: string;
  license: unknown;
  bytes: number;
  httpStatus: number;
  productVersion: string;
}
export interface ResultPage<T> {
  status: 'AVAILABLE' | 'EMPTY';
  items: T[];
  total: number;
  offset: number;
  limit: number;
}
export interface Summary {
  status: 'AVAILABLE' | 'EMPTY';
  counts: { candidates: number; linked: number; unlinked: number; eventReferences: number };
  stations: Station[];
  areas: { areaRef: string; name: string; bbox: Bbox }[];
  warning: string;
}
export const STATUS_LABELS: Record<AssociationStatus, string> = {
  WEATHER_CONTEXT_LINKED: '已关联天气背景',
  NO_QUALIFIED_WEATHER: '无合格天气',
  SATELLITE_ONLY: '仅卫星资料',
};
/** Preserve every supplied predicate (including unknown/duplicate keys) for typed API validation. */
export function scopedQuery(search: string): URLSearchParams {
  const input = new URLSearchParams(search);
  input.delete('offset');
  return input;
}
export function candidatePageQuery(scope: string, offset: number): string {
  const query = new URLSearchParams(scope);
  query.append('limit', '8');
  query.append('offset', String(offset));
  return query.toString();
}
export function signalHref(search: string, candidateId?: string): string {
  const query = new URLSearchParams(search).toString();
  return (
    FIRE_SIGNALS_ROOT +
    (candidateId ? '/candidates/' + encodeURIComponent(candidateId) : '') +
    (query ? '?' + query : '')
  );
}
export function parseBbox(raw: string | null): Bbox | null {
  if (!raw || raw.split(',').some((v) => !v.trim())) return null;
  const values = raw.split(',').map(Number);
  if (values.length !== 4 || values.some((v) => !Number.isFinite(v))) return null;
  const [w, s, e, n] = values;
  if (
    w === undefined ||
    s === undefined ||
    e === undefined ||
    n === undefined ||
    Math.abs(w) > 180 ||
    Math.abs(e) > 180 ||
    Math.abs(s) > 90 ||
    Math.abs(n) > 90 ||
    s >= n ||
    w === e
  )
    return null;
  return [w, s, e, n];
}
export function longitudeSpan(box: Bbox): number {
  return box[2] > box[0] ? box[2] - box[0] : 360 - box[0] + box[2];
}
export function projectPoint(
  longitude: number,
  latitude: number,
  box: Bbox,
): [number, number] | null {
  const span = longitudeSpan(box);
  const delta = (((longitude - box[0]) % 360) + 360) % 360;
  if (delta > span || latitude < box[1] || latitude > box[3]) return null;
  return [60 + (delta / span) * 880, 30 + ((box[3] - latitude) / (box[3] - box[1])) * 400];
}
export function wrapLongitude(value: number): number {
  return ((((value + 180) % 360) + 360) % 360) - 180;
}
export function zoomBbox(box: Bbox, factor: number): Bbox {
  const width = Math.min(359.999, Math.max(0.01, longitudeSpan(box) * factor));
  const height = Math.min(180, Math.max(0.01, (box[3] - box[1]) * factor));
  const centerX = box[0] + longitudeSpan(box) / 2;
  const centerY = Math.max(-90 + height / 2, Math.min(90 - height / 2, (box[1] + box[3]) / 2));
  return [
    wrapLongitude(centerX - width / 2),
    centerY - height / 2,
    wrapLongitude(centerX + width / 2),
    centerY + height / 2,
  ];
}
export function safeSourceUrl(value: string): string | undefined {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' &&
      !url.username &&
      !url.password &&
      !Array.from(url.searchParams.keys()).some((k) => /key|token|secret/i.test(k))
      ? url.href
      : undefined;
  } catch {
    return undefined;
  }
}
